// Real C# Code Execution Service using Judge0 API

// Multiple API endpoints for redundancy
const API_ENDPOINTS = {
  rapidapi: {
    url: 'https://judge0-ce.p.rapidapi.com/submissions',
    headers: (key) => ({
      'Content-Type': 'application/json',
      'X-RapidAPI-Key': key,
      'X-RapidAPI-Host': 'judge0-ce.p.rapidapi.com'
    })
  },
  ce_official: {
    url: 'https://ce.judge0.com/submissions',
    headers: () => ({
      'Content-Type': 'application/json'
    })
  },
  sulu_official: {
    url: 'https://sulu.judge0.com/submissions',
    headers: () => ({
      'Content-Type': 'application/json'
    })
  }
};

// Language IDs for Judge0
const LANGUAGE_IDS = {
  csharp: 51,  // C# (Mono)
  python: 71,
  javascript: 63,
  java: 62
};

/**
 * Execute C# code using Judge0 API with multiple endpoint fallback
 */
export const executeCode = async (code, input = '', language = 'csharp') => {
  const apiKey = process.env.REACT_APP_RAPIDAPI_KEY;
  
  console.log('🔑 API Key loaded:', apiKey ? 'YES ✅ ' + apiKey.substring(0, 20) + '...' : 'NO ❌');
  console.log('🌍 Environment:', process.env.NODE_ENV);
  
  // Try multiple endpoints in order
  // If user has API key, try RapidAPI first, then free public instances
  // If no API key, only try free public instances
  const endpoints = apiKey 
    ? ['rapidapi', 'ce_official', 'sulu_official']
    : ['ce_official', 'sulu_official'];

  for (const endpointName of endpoints) {
    try {
      const endpoint = API_ENDPOINTS[endpointName];
      const headers = endpoint.headers(apiKey);

      console.log(`Trying ${endpointName} endpoint...`);

      const submissionResponse = await fetch(`${endpoint.url}?base64_encoded=false&wait=true`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({
          language_id: LANGUAGE_IDS[language],
          source_code: code,
          stdin: input || '',
          cpu_time_limit: 5,
          memory_limit: 256000
        })
      });

      if (!submissionResponse.ok) {
        const errorText = await submissionResponse.text();
        console.error(`❌ ${endpointName} failed with status:`, submissionResponse.status);
        console.error('Error details:', errorText);
        continue; // Try next endpoint
      }

      const result = await submissionResponse.json();
      console.log('✅ Execution result from', endpointName + ':', result);

      // Parse the result
      return parseExecutionResult(result);
      
    } catch (error) {
      console.error(`${endpointName} error:`, error);
      continue; // Try next endpoint
    }
  }

  // All endpoints failed
  return {
    success: false,
    output: '',
    error: apiKey 
      ? 'All execution services failed.\n\n🔧 Troubleshooting:\n1. Go to: https://rapidapi.com/judge0-official/api/judge0-ce\n2. Make sure you\'re logged in\n3. Click "Subscribe to Test" button\n4. Select "BASIC" plan (FREE)\n5. Click "Subscribe"\n6. Wait 10 seconds and try again\n\nIf already subscribed, the free public instances may be down. Try again later.'
      : 'Code execution unavailable.\n\n📝 To enable:\n1. Sign up at: https://rapidapi.com/judge0-official/api/judge0-ce\n2. Subscribe to FREE "Basic" plan\n3. Get your API key\n4. Add to .env: REACT_APP_RAPIDAPI_KEY=your_key\n5. Restart app',
    time: null,
    memory: null
  };
};

/**
 * Parse Judge0 execution result
 */
function parseExecutionResult(result) {
  // Status codes: https://ce.judge0.com/#statuses-and-languages-status-get
  const statusId = result.status?.id || result.status_id;
  
  if (!statusId) {
    return {
      success: false,
      output: '',
      error: 'Invalid response from execution service',
      time: null,
      memory: null
    };
  }

  // Success (3 = Accepted)
  if (statusId === 3) {
    return {
      success: true,
      output: result.stdout || '(No output)',
      error: null,
      time: result.time,
      memory: result.memory
    };
  }

  // Compilation Error (6)
  if (statusId === 6) {
    return {
      success: false,
      output: '',
      error: `Compilation Error:\n\n${result.compile_output || result.stderr || 'Failed to compile'}`,
      time: null,
      memory: null
    };
  }

  // Runtime Error (NZEC = 5, Runtime Error = 11, 12)
  if ([5, 11, 12].includes(statusId)) {
    return {
      success: false,
      output: result.stdout || '',
      error: `Runtime Error:\n\n${result.stderr || result.message || 'Code crashed during execution'}`,
      time: result.time,
      memory: result.memory
    };
  }

  // Time Limit Exceeded (4)
  if (statusId === 4) {
    return {
      success: false,
      output: result.stdout || '',
      error: 'Time Limit Exceeded: Code took too long to execute (>5 seconds)',
      time: result.time,
      memory: result.memory
    };
  }

  // Memory Limit Exceeded (13)
  if (statusId === 13) {
    return {
      success: false,
      output: result.stdout || '',
      error: 'Memory Limit Exceeded: Code used too much memory',
      time: result.time,
      memory: result.memory
    };
  }

  // Other errors
  return {
    success: false,
    output: result.stdout || '',
    error: result.status?.description || result.stderr || `Error (Status ${statusId})`,
    time: result.time,
    memory: result.memory
  };
}

/**
 * Simple execution wrapper (kept for backward compatibility)
 */
export const executeCodeFree = executeCode;

/**
 * Execute code with retry logic
 */
export const executeCodeWithRetry = async (code, input = '', language = 'csharp', maxRetries = 2) => {
  let lastError = null;
  
  for (let i = 0; i < maxRetries; i++) {
    try {
      const result = await executeCode(code, input, language);
      if (result.success || result.error?.includes('Compilation')) {
        return result; // Don't retry compilation errors
      }
      lastError = result.error;
    } catch (error) {
      lastError = error.message;
    }
    
    // Wait before retry
    if (i < maxRetries - 1) {
      await new Promise(resolve => setTimeout(resolve, 1000 * (i + 1)));
    }
  }
  
  return {
    success: false,
    output: '',
    error: lastError || 'Failed after retries',
    time: null,
    memory: null
  };
};

export default {
  executeCode,
  executeCodeFree,
  executeCodeWithRetry
};

